import { getRecurrenceDatesForDay, getWeeklyRecurrenceScanLimit } from './recurrence';

function parseOccurrenceId(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

function toOccurrenceId(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function splitCompoundId(value) {
  if (typeof value !== 'string' || !value.includes('__')) {
    return { baseId: value || '', occurrenceId: null };
  }
  const parts = value.split('__').filter(Boolean);
  if (!parts.length) return { baseId: value, occurrenceId: null };

  const occurrencePart = parts.find((part) => parseOccurrenceId(part));
  if (!occurrencePart) {
    return { baseId: value, occurrenceId: null };
  }
  return {
    baseId: parts.slice(0, parts.indexOf(occurrencePart)).join('__') || value,
    occurrenceId: occurrencePart,
  };
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function pickNewest(existing, candidate) {
  if (!existing) return candidate;
  const existingTs = toMillis(existing?.data?.updatedAt);
  const candidateTs = toMillis(candidate?.data?.updatedAt);
  return candidateTs >= existingTs ? candidate : existing;
}

function shouldInclude(data) {
  if (String(data?.status || '').toLowerCase() === 'cancelled') return false;
  if (data.attendanceMode === 'rsvp' && data.rsvpStatus !== 'yes') return false;
  if (data.attendanceMode === 'ticketed' && data.ticketStatus !== 'paid') return false;
  return true;
}

function isOccurrenceRsvpNo(data) {
  return (
    data?.attendanceMode === 'rsvp' &&
    data?.scope === 'occurrence' &&
    data?.rsvpStatus === 'no'
  );
}

function normalizeBaseEventId(data, docId) {
  const original = typeof data?.originalEventId === 'string' ? data.originalEventId.trim() : '';
  if (original) return splitCompoundId(original).baseId || original;

  const eventId = typeof data?.eventId === 'string' ? data.eventId.trim() : '';
  if (eventId) return splitCompoundId(eventId).baseId || eventId;

  return splitCompoundId(docId).baseId || docId;
}

function normalizeOccurrenceId(data, docId) {
  const explicit = parseOccurrenceId(data?.occurrenceId);
  if (explicit) return explicit;

  const fromEventId = splitCompoundId(data?.eventId || '').occurrenceId;
  if (fromEventId) return fromEventId;

  const fromOriginalEventId = splitCompoundId(data?.originalEventId || '').occurrenceId;
  if (fromOriginalEventId) return fromOriginalEventId;

  const fromDocId = splitCompoundId(docId || '').occurrenceId;
  if (fromDocId) return fromDocId;

  if (data?.scope === 'occurrence') {
    return parseOccurrenceId(data?.date);
  }

  return null;
}

function resolveStart(data, occurrenceId = null) {
  if (data?.date && data?.time) {
    const d = new Date(`${data.date}T${data.time}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (data?.start?.toDate) {
    const d = data.start.toDate();
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (data?.start) {
    const d = new Date(data.start);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (occurrenceId) {
    const time =
      (typeof data?.time === 'string' && data.time.includes(':') && data.time) ||
      (typeof data?.recurrence?.time === 'string' &&
      data.recurrence.time.includes(':') &&
      data.recurrence.time) ||
      '00:00';
    const d = new Date(`${occurrenceId}T${time}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function buildEvent({
  id,
  title,
  start,
  durationMinutes,
  data,
  baseEventId,
  occurrenceId,
}) {
  return {
    id,
    title: title || 'Event',
    start,
    end: new Date(start.getTime() + (Number(durationMinutes || 60) * 60 * 1000)),
    details: {
      ...data,
      originalId: baseEventId,
      originalEventId: baseEventId,
      occurrenceId: occurrenceId || null,
    },
  };
}

export function buildMirrorCalendarEvents(rows, options = {}) {
  const now = options.now instanceof Date ? options.now : new Date();
  const maxOccurrences = Number.isFinite(options.maxOccurrences) ? options.maxOccurrences : 24;
  const seriesMode = options.seriesMode === 'next' ? 'next' : 'full';
  const rsvpSeriesResponsesByTeam =
    options.rsvpSeriesResponsesByTeam && typeof options.rsvpSeriesResponsesByTeam === 'object'
      ? options.rsvpSeriesResponsesByTeam
      : {};

  const overridesByKey = new Map();
  const exclusionsBySeries = new Map();
  const nonOverrideRows = [];

  rows.forEach((row) => {
    const data = row?.data || {};

    const baseEventId = normalizeBaseEventId(data, row.id);
    const occurrenceId = normalizeOccurrenceId(data, row.id);
    const sourceKey = String(data?.eventId || row.id || '');
    const originalKey = String(data?.originalEventId || '');
    const rowKey = String(row.id || '');
    const hasCompoundOccurrenceKey = sourceKey.includes('__') || originalKey.includes('__') || rowKey.includes('__');
    const isOccurrenceLike =
      data?.scope === 'occurrence' ||
      (Boolean(occurrenceId) && hasCompoundOccurrenceKey);

    const withMeta = {
      id: row.id,
      data,
      baseEventId,
      occurrenceId,
      isOccurrenceLike,
    };

    if (isOccurrenceLike && baseEventId && occurrenceId) {
      if (!exclusionsBySeries.has(baseEventId)) exclusionsBySeries.set(baseEventId, new Set());
      exclusionsBySeries.get(baseEventId).add(occurrenceId);

      if (isOccurrenceRsvpNo(data)) return;
      if (!shouldInclude(data)) return;

      const key = `${baseEventId}__${occurrenceId}`;
      overridesByKey.set(key, pickNewest(overridesByKey.get(key), withMeta));
      return;
    }

    if (!shouldInclude(data)) return;
    nonOverrideRows.push(withMeta);
  });

  const expanded = [];

  nonOverrideRows.forEach((row) => {
    const { data, id, baseEventId } = row;
    const byDay = data?.recurrence?.byDay;
    const recurrenceTime = data?.recurrence?.time;
    const isSeries = Array.isArray(byDay) && byDay.length > 0 && typeof recurrenceTime === 'string';
    const overrideDates = exclusionsBySeries.get(baseEventId) || new Set();
    const teamId = typeof data?.teamId === 'string' ? data.teamId.trim() : '';
    const rsvpSeriesResponsesEnabled = teamId ? rsvpSeriesResponsesByTeam[teamId] !== false : true;

    if (isSeries && data?.attendanceMode === 'rsvp' && data?.scope !== 'occurrence' && !rsvpSeriesResponsesEnabled) {
      return;
    }

    if (isSeries && data?.scope !== 'occurrence') {
      const byDayCodes = Array.from(
        new Set(
          byDay.filter((code) => ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].includes(code))
        )
      );
      if (!byDayCodes.length) return;

      const scanLimit = Math.max(
        1,
        getWeeklyRecurrenceScanLimit({
          now,
          recurrenceEndDate: data?.recurrence?.endDate,
          fallbackOccurrences: maxOccurrences,
        })
      );

      const buildDatesForDay = (dayCode) => getRecurrenceDatesForDay({
        now,
        dayCode,
        recurrence: { ...data.recurrence, frequency: data.recurrence.frequency || data.frequency || 'WEEKLY', weekOfMonth: data.recurrence.weekOfMonth || data.weekOfMonth },
        maxOccurrences: scanLimit,
      });

      if (seriesMode === 'next') {
        let nextMatch = null;

        byDayCodes.forEach((dayCode) => {
          for (const d of buildDatesForDay(dayCode)) {
            if (d <= now) continue;

            const occurrenceId = toOccurrenceId(d);
            if (occurrenceId && overrideDates.has(occurrenceId)) continue;

            if (!nextMatch || d < nextMatch.start) {
              nextMatch = { start: new Date(d), occurrenceId };
            }
            break;
          }
        });

        if (nextMatch?.start) {
          expanded.push(
            buildEvent({
              id: nextMatch.occurrenceId
                ? `${baseEventId}__${nextMatch.occurrenceId}`
                : `${id}-next`,
              title: data?.name || data?.title,
              start: nextMatch.start,
              durationMinutes: data?.durationMinutes,
              data,
              baseEventId,
              occurrenceId: nextMatch.occurrenceId,
            })
          );
        }
        return;
      }

      byDayCodes.forEach((dayCode) => {
        buildDatesForDay(dayCode).forEach((d, i) => {

          const occurrenceId = toOccurrenceId(d);
          if (occurrenceId && overrideDates.has(occurrenceId)) return;

          expanded.push(
            buildEvent({
              id: occurrenceId ? `${baseEventId}__${occurrenceId}` : `${id}-${dayCode}-${i}`,
              title: data?.name || data?.title,
              start: new Date(d),
              durationMinutes: data?.durationMinutes,
              data,
              baseEventId,
              occurrenceId,
            })
          );
        });
      });
      return;
    }

    const start = resolveStart(data, row.occurrenceId);
    if (!start) return;
    const occurrenceId =
      (row.isOccurrenceLike && row.occurrenceId) ||
      (data?.scope === 'occurrence' ? toOccurrenceId(start) : null);
    const eventId = occurrenceId ? `${baseEventId}__${occurrenceId}` : id;
    expanded.push(
      buildEvent({
        id: eventId,
        title: data?.name || data?.title,
        start,
        durationMinutes: data?.durationMinutes,
        data,
        baseEventId,
        occurrenceId,
      })
    );
  });

  overridesByKey.forEach((row, key) => {
    const start = resolveStart(row.data, row.occurrenceId);
    if (!start) return;
    expanded.push(
      buildEvent({
        id: key,
        title: row.data?.name || row.data?.title,
        start,
        durationMinutes: row.data?.durationMinutes,
        data: row.data,
        baseEventId: row.baseEventId,
        occurrenceId: row.occurrenceId,
      })
    );
  });

  const deduped = new Map();
  expanded.forEach((evt) => deduped.set(evt.id, evt));
  return Array.from(deduped.values());
}
